import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ResultsTable = ({ invoiceResults }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'status', direction: 'asc' });

  const toggleRowExpansion = (invoiceId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(invoiceId)) {
      newExpanded?.delete(invoiceId);
    } else {
      newExpanded?.add(invoiceId);
    }
    setExpandedRows(newExpanded);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedResults = [...invoiceResults]?.sort((a, b) => {
    if (sortConfig?.key === 'status') {
      const statusOrder = { 'failed': 0, 'warning': 1, 'success': 2 };
      const aValue = statusOrder?.[a?.status];
      const bValue = statusOrder?.[b?.status];
      return sortConfig?.direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    if (sortConfig?.key === 'processingTime') {
      return sortConfig?.direction === 'asc' 
        ? a?.processingTime - b?.processingTime 
        : b?.processingTime - a?.processingTime;
    }
    
    if (sortConfig?.key === 'errorCount') {
      return sortConfig?.direction === 'asc' 
        ? a?.errorCount - b?.errorCount 
        : b?.errorCount - a?.errorCount;
    }
    
    const aValue = a?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    const bValue = b?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    return sortConfig?.direction === 'asc' 
      ? aValue?.localeCompare(bValue) 
      : bValue?.localeCompare(aValue);
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <Icon name="CheckCircle" size={16} color="var(--color-success)" />;
      case 'failed':
        return <Icon name="XCircle" size={16} color="var(--color-error)" />;
      case 'warning':
        return <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />;
      default:
        return <Icon name="Circle" size={16} color="var(--color-text-secondary)" />;
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'success':
        return `${baseClasses} bg-success/10 text-success border border-success/20`;
      case 'failed':
        return `${baseClasses} bg-error/10 text-error border border-error/20`;
      case 'warning':
        return `${baseClasses} bg-warning/10 text-warning border border-warning/20`;
      default:
        return `${baseClasses} bg-muted text-muted-foreground border border-border`;
    }
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 text-left font-medium text-foreground hover:text-accent transition-colors duration-150"
    >
      <span>{children}</span>
      <Icon 
        name={sortConfig?.key === column && sortConfig?.direction === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
        size={14} 
        color="currentColor"
        className={sortConfig?.key === column ? 'opacity-100' : 'opacity-40'}
      />
    </button>
  );

  return (
    <div className="bg-muted rounded-lg border border-border shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">Invoice Processing Results</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Detailed breakdown of individual invoice validation outcomes
        </p>
      </div>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left">
                <SortButton column="filename">Filename</SortButton>
              </th>
              <th className="px-6 py-3 text-left">
                <SortButton column="status">Status</SortButton>
              </th>
              <th className="px-6 py-3 text-left">
                <SortButton column="stepsCompleted">Steps</SortButton>
              </th>
              <th className="px-6 py-3 text-left">
                <SortButton column="errorCount">Errors</SortButton>
              </th>
              <th className="px-6 py-3 text-left">
                <SortButton column="processingTime">Duration</SortButton>
              </th>
              <th className="px-6 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedResults?.map((invoice) => (
              <React.Fragment key={invoice?.id}>
                <tr className="hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="FileText" size={16} color="var(--color-text-secondary)" />
                      <span className="font-medium text-foreground">{invoice?.filename}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={getStatusBadge(invoice?.status)}>
                      {getStatusIcon(invoice?.status)}
                      <span className="capitalize">{invoice?.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-foreground">{invoice?.stepsCompleted}/5</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={invoice?.errorCount > 0 ? 'text-error font-medium' : 'text-muted-foreground'}>
                      {invoice?.errorCount}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-muted-foreground">{invoice?.processingTime}s</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      {(invoice?.status === 'failed' || invoice?.status === 'warning') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleRowExpansion(invoice?.id)}
                          iconName={expandedRows?.has(invoice?.id) ? "ChevronUp" : "ChevronDown"}
                          iconPosition="right"
                        >
                          Details
                        </Button>
                      )}
                      {invoice?.status === 'failed' && (
                        <Button variant="outline" size="sm" iconName="RotateCcw">
                          Retry
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedRows?.has(invoice?.id) && (invoice?.status === 'failed' || invoice?.status === 'warning') && (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 bg-muted/30">
                      <div className="space-y-3">
                        <h4 className="font-medium text-foreground">
                          {invoice?.status === 'failed' ? 'Error Details' : 'Warning Details'}
                        </h4>
                        <div className="space-y-2">
                          {invoice?.errors?.map((error, index) => (
                            <div key={index} className="flex items-start space-x-2 p-3 bg-muted rounded border border-border">
                              <Icon 
                                name={invoice?.status === 'failed' ? "XCircle" : "AlertTriangle"} 
                                size={16} 
                                color={invoice?.status === 'failed' ? "var(--color-error)" : "var(--color-warning)"}
                                className="mt-0.5 flex-shrink-0"
                              />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-foreground">{error?.step}</p>
                                <p className="text-sm text-muted-foreground mt-1">{error?.message}</p>
                                {error?.recommendation && (
                                  <p className="text-sm text-accent mt-2">
                                    <strong>Recommendation:</strong> {error?.recommendation}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {sortedResults?.map((invoice) => (
          <div key={invoice?.id} className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="FileText" size={16} color="var(--color-text-secondary)" />
                <span className="font-medium text-foreground">{invoice?.filename}</span>
              </div>
              <div className={getStatusBadge(invoice?.status)}>
                {getStatusIcon(invoice?.status)}
                <span className="capitalize">{invoice?.status}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Steps:</span>
                <span className="ml-2 text-foreground">{invoice?.stepsCompleted}/5</span>
              </div>
              <div>
                <span className="text-muted-foreground">Duration:</span>
                <span className="ml-2 text-foreground">{invoice?.processingTime}s</span>
              </div>
              <div>
                <span className="text-muted-foreground">Errors:</span>
                <span className={`ml-2 ${invoice?.errorCount > 0 ? 'text-error font-medium' : 'text-muted-foreground'}`}>
                  {invoice?.errorCount}
                </span>
              </div>
            </div>

            {(invoice?.status === 'failed' || invoice?.status === 'warning') && (
              <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => toggleRowExpansion(invoice?.id)}
                  iconName={expandedRows?.has(invoice?.id) ? "ChevronUp" : "ChevronDown"}
                  iconPosition="right"
                >
                  Details
                </Button>
                {invoice?.status === 'failed' && (
                  <Button variant="outline" size="sm" iconName="RotateCcw">
                    Retry
                  </Button>
                )}
              </div>
            )}

            {expandedRows?.has(invoice?.id) && (
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                {invoice?.errors?.map((error, index) => (
                  <div key={index} className="p-3 bg-muted rounded border border-border">
                    <div className="flex items-start space-x-2">
                      <Icon 
                        name={invoice?.status === 'failed' ? "XCircle" : "AlertTriangle"} 
                        size={14} 
                        color={invoice?.status === 'failed' ? "var(--color-error)" : "var(--color-warning)"}
                        className="mt-0.5 flex-shrink-0"
                      />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-foreground">{error?.step}</p>
                        <p className="text-xs text-muted-foreground mt-1">{error?.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResultsTable;

